import { Component, effect, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { WorkplaceDetailPageStore } from './workplace-detail-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FormBuilder, Validators } from '@angular/forms';
import { isInvalid } from '../../../../shared/helpers/validate';
import { ActivatedRoute, Router } from '@angular/router';
import { contentGlobalSuccess } from '../../../content.error';
import { WorkplaceCreateInput } from '@libs/types/src/services/input-type/services/workplace-create.input';
import { WorkplaceUpdateInput } from '@libs/types/src/services/input-type/services/workplace-update.input';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import { TranslateService } from '@ngx-translate/core';
import { FilterBuilder } from '../../../helper/filter.builder';
import { dateify, dateifyOrNow } from '../../../../shared/helpers/date.helper';
import { selectService } from '../../../store/service/service.selectors';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { CardModule } from '@openng/optimus-ui/card';
import { DividerModule } from '@openng/optimus-ui/divider';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { PhoneInputComponent } from '../../../../../components/kit/input/phone-input/phone-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import {
    WorkplaceDetailAddressesComponent,
} from '../../../components/services/workplace-detail-addresses/workplace-detail-addresses.component';
import {
    WorkplaceDetailIcpsComponent,
} from '../../../components/services/workplace-detail-icps/workplace-detail-icps.component';
import {
    WorkplaceDetailResponsiblePersonsComponent,
} from '../../../components/services/workplace-detail-responsible-persons/workplace-detail-responsible-persons.component';

@Component({
    selector: 'app-workplace-detail-page',
    templateUrl: 'workplace-detail-page.component.html',
    styleUrl: 'workplace-detail-page.component.scss',
    providers: [WorkplaceDetailPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        CardModule,
        DateInputComponent,
        DividerModule,
        EmailInputComponent,
        PhoneInputComponent,
        TextInputComponent,
        TranslatePipe,
        WorkplaceDetailAddressesComponent,
        WorkplaceDetailIcpsComponent,
        WorkplaceDetailResponsiblePersonsComponent,
    ],
})
export class WorkplaceDetailPageComponent extends SubscriptionComponent implements OnInit {

    protected readonly store = inject(WorkplaceDetailPageStore);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly breadcrumbs = inject(BreadcrumbService);
    private readonly translate = inject(TranslateService);

    protected prefix = 'service.workplace.';

    protected form = this.fb.group({
        name: this.fb.control('', Validators.required),
        dateFrom: this.fb.control(new Date(), Validators.required),
        dateTo: this.fb.control<Date | null>(null),
        email: this.fb.control<string | null>(null, Validators.email),
        phone: this.fb.control<string | null>(null),
        defaultIcp: this.fb.control<string | null>(null),
    });

    protected workplaceId: number | null = null;

    protected hideDefaultIcp = false;

    protected service = this.store!.globalStore!.selectSignal(selectService);

    constructor() {
        super();

        effect(() => {
            if (this.service()?.type === ServiceTypeEnum.HEALTH_CARE) {
                this.form.get('defaultIcp')?.setValidators([Validators.required, Validators.maxLength(8)]);
            }
        });
    }

    public ngOnInit(): void {
        this.subs.push(
            this.store.routeParam.workplaceId.subscribe((it) => {
                this.workplaceId = Number(it) || null;
                if (this.workplaceId) {
                    this.store.api.getWorkplaces(FilterBuilder.singleItem({ id: this.workplaceId }));
                } else {
                    this.breadcrumbs.set(`@${BreadcrumbsEnum.Workplace}`, `${BreadcrumbsEnum.Workplace}|${this.translate.instant('workplace.create')}`);
                    this.loadServiceBasicInfo();
                }
            }),
            this.store.workplace$.subscribe((it) => {
                if (it) {
                    this.breadcrumbs.set(`@${BreadcrumbsEnum.Workplace}`, `${BreadcrumbsEnum.Workplace}|${it?.name}`);
                    this.form.patchValue({
                        ...it,
                        dateFrom: dateifyOrNow(it.dateFrom),
                        dateTo: dateify(it.dateTo),
                    });
                    this.hideDefaultIcp = it.anyIcpForInsuranceCompanySet;
                }
            }),
            this.store.serviceBasicInfo$.subscribe((serviceBasicInfo) => {
                if (serviceBasicInfo) {
                    this.form.patchValue({
                        defaultIcp: this.generateICP(serviceBasicInfo.icz),
                    });
                }
            }),
            this.store.apiSuccess.createWorkplace.subscribe(async () => {
                contentGlobalSuccess.next('workplace.message.created');
                await this.goBack();
            }),
            this.store.apiSuccess.updateWorkplace.subscribe(async () => {
                contentGlobalSuccess.next('workplace.message.updated');
                await this.goBack();
            }),
        );
    }

    protected async save(): Promise<void> {
        if (isInvalid(this.form)) return;
        const base = {
            ...this.form.value,
            dateFrom: this.form.value.dateFrom?.toISOString() || null,
            dateTo: this.form.value.dateTo?.toISOString() || null,
        };

        if (this.workplaceId) {
            const data = base as WorkplaceUpdateInput;
            data.id = this.workplaceId;
            this.store.api.updateWorkplace(data);
        } else {
            const data = base as WorkplaceCreateInput;
            data.serviceId = Number(this.store.routeParam.serviceId.value);
            this.store.api.createWorkplace(data);
        }
    }

    protected async goBack(): Promise<void> {
        await this.router.navigate(['../..'], { relativeTo: this.route });
    }

    private loadServiceBasicInfo() {
        const serviceId = Number(this.route.snapshot.params['serviceId']) || null;
        if (serviceId) {
            const filter = new FilterBuilder();
            filter.where('serviceId', serviceId, 'EQ');
            filter.where('validFrom', new Date().toISOString(), 'LTE');
            filter.sort('validFrom', 'DESC');
            filter.limit(1);

            this.store.api.getServiceBasicInfos(filter.filter());
        }
    }

    private generateICP(serviceIcz: string | null): string {
        if (!serviceIcz) return '';

        const serialNumber = Number(history.state['numberOfWorkplaces']) ?? 0;

        return serviceIcz.substring(0, 4) + serialNumber.toString().padStart(3, '0');
    }

    protected readonly ServiceTypeEnum = ServiceTypeEnum;
}
